// @flow

import find from 'lodash/find';

import * as urls from 'app/urls';
import * as request from 'app/utils/request';
import {gt, ngt} from 'app/utils/i18n';

import {
  showNotification,
  showUpgradeModal,
  UPGRADE_MODALS,
} from 'app/toasts/slice';
import type {BasicTeamType, TeamType} from 'app/teams/types';
import type {UserTeamMembershipType} from 'app/user/types';
import {limitRequests} from 'app/utils/redux';
import type {TeamsFetchParamsType, TeamsLocationParamsType} from './types';
import type {ResponseType} from 'app/utils/redux_v2';

import {convertLocationParamsToFetchParams} from './utils';
import {requestTeams} from './fetchers';

export const RECEIVE_TEAM_DETAIL = 'RECEIVE_TEAM_DETAIL';
export const SET_TEAM_PAGE_FILTERS = 'SET_TEAM_PAGE_FILTERS';

const getFetchErrorMsg = () => gt('There was an error fetching the teams');

/* sync actions */

export const receiveDetail = (item: TeamType) => ({
  type: RECEIVE_TEAM_DETAIL,
  item,
});

export const setTeamPageFilters = (team: TeamType, filters: {}) => ({
  type: SET_TEAM_PAGE_FILTERS,
  team,
  filters,
});

/* async actions */

/* team(s) fetch */

export const RECEIVE_TEAMS_LIST = 'RECEIVE_TEAMS_LIST';

type ReceiveTeamsListType = {
  type: 'RECEIVE_TEAMS_LIST',
  query: TeamsFetchParamsType,
  response: ResponseType<TeamType>,
};

export const receiveTeamsList = (
  query: TeamsFetchParamsType,
  response: ResponseType<TeamType>
): ReceiveTeamsListType => ({type: RECEIVE_TEAMS_LIST, query, response});

export const fetchTeams = limitRequests(
  (locationParams: TeamsLocationParamsType) => (dispatch: SimpleDispatch) => {
    const fetchParams = convertLocationParamsToFetchParams(locationParams);

    return requestTeams(fetchParams).then(
      (response) => dispatch(receiveTeamsList(fetchParams, response)),
      request.createErrorHandler(getFetchErrorMsg(), dispatch)
    );
  }
);

export const fetchTeamDetails =
  (teamId: number, userNotFoundAction?: () => void) =>
  (dispatch: SimpleDispatch) => {
    return request
      .get(`${urls.TEAMS_API_URL}${teamId}/`)
      .then((data) => dispatch(receiveDetail(data)))
      .catch(
        request.createErrorHandler(getFetchErrorMsg(), dispatch, {
          '404': () => {
            userNotFoundAction !== undefined && userNotFoundAction();
            dispatch(
              showNotification(gt('Team not found in the account'), 'error')
            );
          },
        })
      );
  };

/* generic remove */

const removeFromTeam =
  <T: {+team: string, +url: string}>(team: TeamType, items: Array<T>) =>
  (dispatch: FuncDispatch): Promise<any> => {
    const membership = find(items, {team: team.url});

    if (membership != null) {
      return request
        .del(membership.url)
        .then(() => {
          dispatch(showNotification(gt('Removed from team')));
        })
        .catch(
          request.createErrorHandler(
            gt('Could not remove from team.'),
            dispatch,
            {
              '404': () => {
                dispatch(showNotification(gt('Not in the team'), 'error'));
              },
              ...request.showNonFieldErrorMessageHandler(
                false,
                'Could not remove from team'
              ),
            }
          )
        );
    } else {
      return Promise.resolve(
        dispatch(showNotification(gt('Not in the team'), 'error'))
      );
    }
  };

/* team changes */

export const updateTeam =
  (teamId: number, data: {}) => (dispatch: SimpleDispatch) =>
    request
      .patchJson(`${urls.TEAMS_API_URL}${teamId}/`, data)
      .then((team) => {
        dispatch(showNotification(gt('Updated team %s', team.name || '')));
        dispatch(receiveDetail(team));
        return team;
      })
      .catch((error) =>
        request.createErrorHandler(gt('Unable to update team'), dispatch)(error)
      );

export const createTeam =
  (teamName: string, description: string) => (dispatch: SimpleDispatch) =>
    request
      .postJson(urls.TEAMS_API_URL, {
        name: teamName,
        description,
      })
      .then((response) => {
        dispatch(
          showNotification(gt('Successfully created team %s', teamName))
        );
        return response;
      })
      .catch((error) =>
        request.createErrorHandler(gt('Unable to create team'), dispatch, {
          '403': (error) => {
            if (error.data.detail === urls.STATUSES.UPGRADE_REQUIRED) {
              dispatch(showUpgradeModal(UPGRADE_MODALS.TEAM_LIMIT));
            } else {
              dispatch(showNotification(error.data.detail, 'error'));
            }
            throw error;
          },
        })(error)
      );

/* generic */
export const removeItem = <
  T: {
    +team: string,
    +url: string,
  }
>(
  team: TeamType,
  item: {team_memberships: Array<T>}
) => removeFromTeam(team, item.team_memberships);

/* team members */

export const setTeamMemberAccess =
  (membership: UserTeamMembershipType, access: 'manager' | 'teammate' | 'reader') =>
  (dispatch: any) => {
    return request
      .patchJson(membership.url, {access})
      .catch(
        request.createErrorHandler(
          gt(
            'A problem on our server occurred. Unable to update member status.'
          ),
          dispatch
        )
      );
  };

/* team procedures */

export const addProcedures =
  <T: {url: string}>(team: BasicTeamType, procedures: Array<T>) =>
  (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.TEAM_PROCEDURES_API_URL,
        procedures.map((procedure) => ({
          procedure: procedure.url,
          team: team.url,
        }))
      )
      .then(() => {
        dispatch(
          showNotification(
            ngt('Added procedure', 'Added procedures', procedures.length)
          )
        );
      })
      .catch(
        request.createErrorHandler(gt('Could not add procedure.'), dispatch)
      );

/* team processes */
export const addProcesses =
  <T: {url: string}>(team: BasicTeamType, processes: Array<T>) =>
  (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.TEAM_PROCESSES_API_URL,
        processes.map((process) => ({
          process: process.url,
          team: team.url,
        }))
      )
      .then(() => {
        dispatch(
          showNotification(
            ngt('Added process', 'Added processes', processes.length)
          )
        );
      })
      .catch(
        request.createErrorHandler(gt('Could not add process.'), dispatch)
      );

/* team policies */

export const addPolicies =
  <T: {url: string}>(team: BasicTeamType, policies: Array<T>) =>
  (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.TEAM_POLICIES_API_URL,
        policies.map((policy) => ({
          policy: policy.url,
          team: team.url,
        }))
      )
      .then(() => {
        dispatch(
          showNotification(
            ngt('Added policy', 'Added policies', policies.length)
          )
        );
      })
      .catch(request.createErrorHandler(gt('Could not add policy.'), dispatch));

/* team folders */

export const addFolders =
  <T: {url: string}>(team: TeamType, folders: Array<T>) =>
  (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.TEAM_FOLDERS_API_URL,
        folders.map((folder) => ({
          folder: folder.url,
          team: team.url,
        }))
      )
      .then(() => {
        dispatch(
          showNotification(ngt('Added folder', 'Added folders', folders.length))
        );
      })
      .catch(request.createErrorHandler(gt('Could not add folder.'), dispatch));
